<template>
  <router-view />
</template>

<script setup lang="ts">
import { useUserInfoStore } from 'stores/user-info-store';
import { onMounted, watch } from 'vue';
import { useAuth } from './composables/auth/useAuth';
import { useRouter } from "vue-router";
import { globalRouter } from "./router/globalRouter";
import { useAuthStore } from 'src/stores/auth-store';

import { useI18n } from 'vue-i18n';
import { Quasar } from 'quasar';

import langEn from 'quasar/lang/en-US';
import langZh from 'quasar/lang/zh-CN';
import langEs from 'quasar/lang/es';
import langFr from 'quasar/lang/fr';
import langKo from 'quasar/lang/ko-KR';
import langVi from 'quasar/lang/vi';
import langJa from 'quasar/lang/ja';

const { locale } = useI18n();
import { getNormalizedLocale } from './utils/utils';

const quasarLangMap = {
  'en-US': langEn,
  'zh-CN': langZh,
  'es-ES': langEs,
  'fr-FR': langFr,
  'ko-KR': langKo,
  'vi-VN': langVi,
  'ja-JP': langJa,
};

function getQuasarLang(locale) {
  const normalized = getNormalizedLocale(locale);
  return quasarLangMap[normalized] || langEn;
}

Quasar.lang.set(getQuasarLang(locale.value));

watch(locale, (newLocale) => {
  Quasar.lang.set(getQuasarLang(newLocale));
});

const userInfoStore = useUserInfoStore();
const authStore = useAuthStore();
const { refreshToken } = useAuth();
const router = useRouter();
globalRouter.router = router;
// Setup the event listener to check for changes in the isAuthenticated state
//
const checkAuthChange = (event) => {
  if (event.storageArea != localStorage) return;
  if (event.key === "auth") {
    location.reload();
  }
};

watch(() => userInfoStore.language, (newLang) => {
  locale.value = newLang;
});

// try to get a new access token when the app starts
onMounted(async (): Promise<void> => {
  await refreshToken(userInfoStore.id === '' ? true : false);

  const storedUserInfo = JSON.parse(localStorage.getItem('user-info') || '{}');
  // Add missing fields with default values
  const updatedUserInfo = {
    ...storedUserInfo,
    isAdmin: storedUserInfo.isAdmin ?? false,
    agencyLogo: storedUserInfo.agencyLogo ?? '',
    agencyId: storedUserInfo.agencyId ?? '',
    studentLevel: storedUserInfo.studentLevel ?? 1,
    agencyName: storedUserInfo.agencyName ?? '',
  };

  localStorage.setItem('user-info', JSON.stringify(updatedUserInfo));
});

</script>
